import React, { useState, useEffect } from 'react';
import { useNavigate,Link } from 'react-router-dom';
import {
  User, Mail, Lock, GraduationCap, Building, Calendar, School,
  Check, ArrowRight, Users, Briefcase, CalendarClock, Target,X
} from 'lucide-react';
import CollegeDropdown from '../components/CollegeDropDown';
import DepartmentDropdown from '../components/DepartmentDropdown';
import YearPicker from '../components/YearPicker';
import DegreeDropdown from '../components/DegreeDropdown';

const RegisterForm = () => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [collegeSearchTerm, setCollegeSearchTerm] = useState('');
  const [isCollegeDropdownOpen, setIsCollegeDropdownOpen] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDegree, setSelectedDegree] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);


  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    enrollmentNumber: '',
    graduationYear: '',
    admissionYear: '', 
    degree: '',
    department: '',
  });

  const carouselData = [
    {
      title: "Connect with Alumni",
      description: "Join a vibrant community of graduates and expand your professional network",
  image: "https://d8it4huxumps7.cloudfront.net/uploads/images/65799bf7b8ae5_frame_1000013237.png?d=700x700"
    },
    {
      title: "Access Opportunities",
      description: "Discover exclusive job postings and career advancement opportunities",
      image: "https://d8it4huxumps7.cloudfront.net/uploads/images/66a3829b1d2da_jobs_internships.png?d=996x803"
    },
    {
      title: "Stay Updated",
      description: "Get the latest news and updates from your alma mater",
image: "https://d8it4huxumps7.cloudfront.net/uploads/images/un-pro/upgrade-banner-image.png?d=813x639"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchColleges = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/list`);
        const data = await response.json();
        setColleges(data);
      } catch (err) {
        setError('Failed to load colleges');
      }
    };
    fetchColleges();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
  
    if (!selectedCollege?._id) {
      setError('Please select a college');
      setLoading(false);
      return;
    }
  
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        enrollmentNumber: formData.enrollmentNumber,
        college: selectedCollege._id,
        graduationYear: parseInt(formData.graduationYear),
        admissionYear: parseInt(formData.admissionYear),
        academicInfo: {
          degree: formData.degree,
          department: formData.department
        }
      };
  
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/alumni/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }
  
      await response.json();
      setShowSuccessModal(true); // Show modal instead of immediate navigation
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentsForDegree = () => {
    if (!selectedDegree || !selectedCollege) return [];
    const degree = selectedCollege.degrees.find(d => d.name === selectedDegree);
    return degree ? degree.departments : [];
  };

  const handleCollegeSelect = (college) => {
    setSelectedCollege(college);
    setCollegeSearchTerm(college.name);
    setIsCollegeDropdownOpen(false);
    setSelectedDegree(null);
    setFormData(prev => ({
      ...prev,
      degree: '',
      department: ''
    }));
  };

  const handleDegreeChange = (degreeName) => {
    setSelectedDegree(degreeName);
    setFormData(prev => ({
      ...prev,
      degree: degreeName,
      department: ''
    }));
  };

  return (
    <div className="min-h-screen relative overflow-hidden py-8">
      {/* Background SVG Pattern */}
      <div className="absolute inset-0 z-0" 
           style={{
             backgroundImage: "url('https://statics.myclickfunnels.com/image/301108/file/215e0050b0709c2f41a105beec3f25ff.svg')",
             backgroundSize: "600px",
             backgroundRepeat: "repeat",
             opacity: 0.15
           }} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-center mb-6">
          <img 
            src="https://i.ibb.co/Y2zGN8s/saarthilogo.png"
            alt="Saarthi Logo"
            className="h-12 w-auto"
          />
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row">
            {/* Left side - Carousel with wave background */}
            <div className="hidden md:block md:w-5/12 bg-[#ffc700] relative overflow-hidden">
              {/* Wave pattern background */}
              <div className="absolute inset-0 z-0 opacity-10"
                   style={{
                     backgroundImage: "url('https://d8it4huxumps7.cloudfront.net/uploads/images/login/border-vector-image.png?d=943x2825')",
                     backgroundSize: "cover",
                     backgroundPosition: "center",
                   }} />
              
              <div className="h-full flex flex-col justify-between relative z-10">
                {/* Carousel Section */}
                <div className="p-8 flex-grow">
                  <div className="h-full flex flex-col">
                    {carouselData.map((slide, index) => (
                      <div
                        key={index}
                        className={`transition-opacity duration-500 ${
                          index === activeSlide ? 'opacity-100' : 'opacity-0 hidden'
                        }`}
                      >
                        <div className="aspect-w-16 aspect-h-9 mb-4">
                          <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-72 object-cover rounded-lg"
                          />
                        </div>
                        <div className="mt-4 text-gray-900">
                          <h3 className="text-lg font-bold">{slide.title}</h3>
                          <p className="mt-2 text-sm text-gray-800">{slide.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer Section with Links */}
<div className="bg-white/90 p-6 backdrop-blur-md mt-auto border-t border-amber-100/50 shadow-lg">
  <div className="text-center space-y-4">
   
    
    <div className="pt-2 border-t border-amber-100">
      <p className="text-sm text-gray-600 mb-2">
        By joining Saarthi, you agree to our
      </p>
      <div className="flex justify-center items-center space-x-4 text-sm font-medium">
        <a 
          href="/terms" 
          className="text-amber-700 hover:text-amber-900 transition-colors hover:underline"
        >
          Terms
        </a>
        <div className="w-1 h-1 rounded-full bg-amber-300"></div>
        <a 
          href="/privacy" 
          className="text-amber-700 hover:text-amber-900 transition-colors hover:underline"
        >
          Privacy
        </a>
        <div className="w-1 h-1 rounded-full bg-amber-300"></div>
        <a 
          href="/community" 
          className="text-amber-700 hover:text-amber-900 transition-colors hover:underline"
        >
          Guidelines
        </a>
      </div>
    </div>
  </div>
</div>
              </div>
            </div>

            {/* Right side - Form with improved scrolling */}
            <div className="w-full md:w-7/12 bg-white">
              <div className="p-6 md:p-8">
                <div className="overflow-y-auto max-h-[70vh] space-y-6 pr-4" 
                     style={{
                       scrollbarWidth: 'thin',
                       scrollbarColor: '#FFC700 transparent',
                       msOverflowStyle: 'none',
                       '::-webkit-scrollbar': {
                         width: '8px',
                       },
                       '::-webkit-scrollbar-track': {
                         background: 'transparent',
                       },
                       '::-webkit-scrollbar-thumb': {
                         background: '#FFC700',
                         borderRadius: '4px',
                       }
                     }}>
                <div className="mb-8">
  <div className="flex items-start space-x-2">
    <h2 className="text-2xl font-bold text-gray-900 mb-1">
      Join the Saarthi Community!
      <span className="ml-2 inline-block animate-wave">✨</span>
    </h2>
  </div>
  
  <div className="space-y-3">
    <p className="text-sm text-gray-600">
      Create your account and unlock a world of opportunities
    </p>
    
    <div className="flex flex-wrap gap-2">
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-amber-50 text-xs font-medium text-amber-700">
        <span className="mr-1">🎓</span> Alumni Network
      </span>
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-xs font-medium text-blue-700">
        <span className="mr-1">💼</span> Job Opportunities
      </span>
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-50 text-xs font-medium text-green-700">
        <span className="mr-1">🤝</span> Mentorship
      </span>
    </div>
  </div>

  <style jsx>{`
    @keyframes wave {
      0% { transform: scale(1); }
      50% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
    .animate-wave {
      animation: wave 2s infinite;
      display: inline-block;
    }
  `}</style>
</div>
                  
                  <form onSubmit={handleSubmit} className="space-y-5">
                 
                    {error && (
                      <div className="p-3 text-sm text-red-800 rounded-lg bg-red-50 border border-red-100">
                        {error}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-amber-600" />
                          </div>
                          <input
                            type="text"
                            required
                            className="pl-9 w-full rounded-md border border-gray-300 bg-white py-2 text-sm text-gray-900 outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-colors"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-amber-600" />
                          </div>
                          <input
                            type="text"
                            required
                            className="pl-9 w-full rounded-md border border-gray-300 bg-white py-2 text-sm text-gray-900 outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-colors"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-amber-600" />
                        </div>
                        <input
                          type="email"
                          required
                          className="pl-9 w-full rounded-md border border-gray-300 bg-white py-2 text-sm text-gray-900 outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-colors"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-4 w-4 text-amber-600" />
                        </div>
                        <input
                          type="password"
                          required
                          className="pl-9 w-full rounded-md border border-gray-300 bg-white py-2 text-sm text-gray-900 outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-colors"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        College
                      </label>
                      <CollegeDropdown
                        colleges={colleges}
                        searchTerm={collegeSearchTerm}
                        onSearchChange={setCollegeSearchTerm}
                        selectedCollege={selectedCollege}
                        onSelect={handleCollegeSelect}
                        isOpen={isCollegeDropdownOpen}
                        setIsOpen={setIsCollegeDropdownOpen}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Enrollment Number
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <GraduationCap className="h-4 w-4 text-amber-600" />
                          </div>
                          <input
                            type="text"
                            required
                            className="pl-9 w-full rounded-md border border-gray-300 bg-white py-2 text-sm text-gray-900 outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-colors"
                            value={formData.enrollmentNumber}
                            onChange={(e) => setFormData({ ...formData, enrollmentNumber: e.target.value })}
                          />
                        </div>
                      </div>
                      </div>

<div className="grid grid-cols-2 gap-4">
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Admission Year
    </label>
    <YearPicker
      value={formData.admissionYear ? parseInt(formData.admissionYear) : ''}
      onChange={(year) => setFormData({ ...formData, admissionYear: year.toString() })}
      minYear={1900}
      maxYear={new Date().getFullYear()}  
    />
  </div>

  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Graduation Year
    </label>
    <YearPicker
      value={formData.graduationYear ? parseInt(formData.graduationYear) : ''}
      onChange={(year) => setFormData({ ...formData, graduationYear: year.toString() })}
      minYear={1900}
      maxYear={new Date().getFullYear() + 5}
    />
  </div>
</div>
                   

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Degree
                      </label>
                      <DegreeDropdown
                        degrees={selectedCollege?.degrees || []}
                        value={formData.degree}
                        onChange={handleDegreeChange}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department
                      </label>
                      <DepartmentDropdown
                        departments={getDepartmentsForDegree()}
                        value={formData.department}
                        onChange={(value) => setFormData({ ...formData, department: value })}
                        disabled={!selectedDegree}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center py-2.5 px-4 rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:bg-amber-300 transition-colors duration-200"
                    >
                      {loading ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-5 h-5 border-t-2 border-white border-solid rounded-full animate-spin" />
                          <span>Creating your account...</span>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <span>Join the Community</span>
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </div>
                      )}
                    </button>

                   
                 
                  </form>
                  
                </div>
                <div className="flex flex-col items-center justify-center space-y-2 pt-4">
      <div className="text-sm">
        <span className="text-gray-600">Already have an account?</span>{' '}
        <Link
          to="/login" 
          className="inline-flex items-center font-semibold text-amber-600 hover:text-amber-800 transition-colors"
        >
          Sign in
          <svg 
            className="w-4 h-4 ml-1 transform transition-transform group-hover:translate-x-1" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </Link>
      </div>
    </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSuccessModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl p-8 max-w-md mx-4 relative animate-fadeIn">
      <div className="absolute top-4 right-4">
        <button
          onClick={() => {
            setShowSuccessModal(false);
            navigate('/login');
          }}
          className="text-gray-400 hover:text-gray-500"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <Check className="h-6 w-6 text-green-600" />
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Registration Successful! 🎉
        </h3>
        
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-500">
            Thank you for joining Saarthi! Your registration request has been submitted successfully.
          </p>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-700">
              Please note that your account needs to be verified by your college administration. You'll receive an email notification once your account is verified.
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            setShowSuccessModal(false);
            navigate('/login');
          }}
          className="mt-6 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
        >
          Go to Login
          <ArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
)}


<style jsx>{`
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }
`}</style>
    </div>
  );
};

export default RegisterForm;